import React, { useEffect, useRef } from "react";
import { Box, Typography } from "@mui/material";
import moment from "moment";
import Shimmer from "../../../../../components/crm-dashboard/Shimmer";

const bubble = (outbound) => ({
  maxWidth: "72%",
  // flex items default to min-width:auto, so a long unbroken word would push
  // the bubble past the thread width and add a horizontal scrollbar
  minWidth: 0,
  alignSelf: outbound ? "flex-end" : "flex-start",
  background: outbound ? "#E7F6EC" : "#EFF4FB",
  border: `1px solid ${outbound ? "#CDEBD8" : "#DCE6F5"}`,
  borderRadius: outbound ? "14px 14px 4px 14px" : "14px 14px 14px 4px",
  px: "14px",
  py: "9px",
});

const DaySeparator = ({ label }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, my: 1.5 }}>
    <Box sx={{ flex: 1, height: "1px", background: "#F0F1F4" }} />
    <Typography sx={{ fontSize: "11px", color: "#9CA3AF", fontWeight: 600 }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, height: "1px", background: "#F0F1F4" }} />
  </Box>
);

const ChatThread = ({ messages, loading, hasMore, onLoadOlder }) => {
  const scrollRef = useRef(null);
  const prevCountRef = useRef(0);

  // Follow the newest message — but never yank the user down while they're
  // reading history. Fires only when the message count actually grows,
  // jumps instantly (an interruptible smooth scroll reads as jerky
  // stepping), and scrolls ONLY this container.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const first = prevCountRef.current === 0 && messages.length > 0;
    const grew = messages.length > prevCountRef.current;
    prevCountRef.current = messages.length;
    if (!grew) return;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (first || distanceFromBottom < 240) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages.length]);

  const handleScroll = () => {
    if (hasMore && scrollRef.current && scrollRef.current.scrollTop === 0) {
      onLoadOlder();
    }
  };

  if (loading && messages.length === 0) {
    return (
      <Box sx={{ flex: 1, p: 2.5 }}>
        <Shimmer width="60%" height={54} radius="14px" sx={{ mb: 1.5 }} />
        <Shimmer width="55%" height={44} radius="14px" sx={{ mb: 1.5, ml: "auto" }} />
        <Shimmer width="65%" height={64} radius="14px" />
      </Box>
    );
  }

  let lastDay = null;

  return (
    <Box
      ref={scrollRef}
      onScroll={handleScroll}
      sx={{
        flex: 1,
        // Flex children default to min-height:auto — without this the thread
        // grows to its content height instead of scrolling, and the wheel
        // scrolls outer containers in odd small steps.
        minHeight: 0,
        overflowY: "auto",
        overflowX: "hidden",
        overscrollBehavior: "contain",
        px: 2.5,
        py: 2,
        display: "flex",
        flexDirection: "column",
        gap: "10px",
        background: "#FCFCFD",
      }}
    >
      {messages.length === 0 && (
        <Typography sx={{ color: "#9CA3AF", fontSize: "14px", textAlign: "center", my: 6 }}>
          No messages yet — start the conversation below.
        </Typography>
      )}
      {messages.map((message) => {
        const outbound = message.direction === "outbound";
        const day = moment(message.created_at).format("MMM D, YYYY");
        const separator = day !== lastDay ? <DaySeparator key={`d-${day}`} label={day} /> : null;
        lastDay = day;
        return (
          <React.Fragment key={message.id}>
            {separator}
            <Box sx={bubble(outbound)}>
              <Typography
                sx={{ fontSize: "11px", color: "#9CA3AF", mb: message.body ? "3px" : 0 }}
              >
                {moment(message.created_at).format("h:mm A")}
                {outbound && message.actor_name ? ` · ${message.actor_name}` : ""}
              </Typography>
              {message.body && (
                <Typography
                  sx={{
                    fontSize: "14px",
                    color: "#1a1a2e",
                    whiteSpace: "pre-wrap",
                    // pre-wrap alone still overflows on words with no spaces
                    // (pasted links, "testingtesting…")
                    overflowWrap: "anywhere",
                    wordBreak: "break-word",
                  }}
                >
                  {message.body}
                </Typography>
              )}
              {(message.media || []).map((url, index) => (
                <Box
                  key={index}
                  component="img"
                  src={url}
                  alt="attachment"
                  sx={{
                    display: "block",
                    width: "auto",
                    maxWidth: "min(220px, 100%)",
                    maxHeight: "220px",
                    borderRadius: "10px",
                    mt: "6px",
                  }}
                />
              ))}
              {outbound && message.status && (
                <Typography sx={{ fontSize: "10px", color: "#9CA3AF", mt: "3px", textAlign: "right" }}>
                  {message.status}
                </Typography>
              )}
            </Box>
          </React.Fragment>
        );
      })}
    </Box>
  );
};

export default ChatThread;
